import { StyleSheet, View } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { Degree, useGetCoursesByDegreeByQueryQuery } from "../../../graphql/generated/graphql";
import AdminTable from "../../../components/adminDashboard/AdminTable/AdminTable";
import { useState } from "react";
import useSortingDropdown from "../../../hooks/useSortingDropdown";
import usePagination from "../../../hooks/usePagination";
import AdminTableHeader from "../../../components/adminDashboard/AdminTable/AdminTableHeader";
import ManageCoursesTable, { ManageCoursesModalState } from "../../../components/adminDashboard/ManageCoursesTable";
import appConfig from "../../../app.config";
import AdminTablePagination from "../../../components/adminDashboard/AdminTable/AdminTablePagination";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { Plus } from "@phosphor-icons/react";
import ManageCoursesModal from "../../../components/adminDashboard/DegreesPrograms/ManageCoursesModal";

const SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
];

const AdminDegreeManageCoursesContainer = ({ degree }: { degree: Degree }) => {
  const [modalState, setModalState] = useState({
    state: ManageCoursesModalState.CLOSED,
    courseId: "",
    courseName: "",
    isCourseProcessing: false,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });

  const {
    data: degreeCoursesData,
    loading: degreeCoursesLoading,
    refetch: degreeCoursesRefetch,
  } = useGetCoursesByDegreeByQueryQuery({
    variables: {
      degreeId: degree.id || "",
      searchQuery,
      page,
      pageSize,
      sortBy,
    },
    skip: !degree?.id,
  });

  const totalCount = degreeCoursesData?.getCoursesByDegreeByQuery?.data.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleActionModalOpen = (
    courseId: string,
    courseName: string,
    modalType: ManageCoursesModalState,
    isCourseProcessing: boolean
  ) => {
    setModalState({
      state: modalType,
      courseId,
      courseName,
      isCourseProcessing,
    });
  };

  const handleModalClose = () => {
    setModalState({ state: ManageCoursesModalState.CLOSED, courseId: "", courseName: "", isCourseProcessing: false });
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <CustomText
            text={`Degree Courses (${degreeCoursesData?.getCoursesByDegreeByQuery?.data.totalCount || 0})`}
            size="l"
            weight="bold"
            style={styles.title}
            useTranslationText={false}
          />
        </View>
        <View style={styles.headerRight}>
          <CustomButton
            text="Add Course to Degree"
            styleType="primary"
            size="m"
            leftIcon={<Plus size={24} color="#FFFFFF" />}
            onPress={() =>
              setModalState({
                state: ManageCoursesModalState.ADD_COURSE,
                courseId: "",
                courseName: "",
                isCourseProcessing: false,
              })
            }
            useTranslationText={false}
            textStyle={{ color: "#FFFFFF", fontWeight: "700", fontSize: 14 }}
            style={styles.addStudentButton}
          />
        </View>
      </View>
      <AdminTable>
        <AdminTableHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDropdownOpen={sortDropdownOpen}
          setSortDropdownOpen={setSortDropdownOpen}
          sortItems={SORT_ITEMS}
        />
        <ManageCoursesTable
          courses={degreeCoursesData?.getCoursesByDegreeByQuery?.data.courses || []}
          onActionModalOpen={handleActionModalOpen}
          hasDegrees={appConfig.hasDegrees}
          returnTo="Degrees & Programs"
        />
        {totalCount > 0 && (
          <AdminTablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
          />
        )}
      </AdminTable>
      <ManageCoursesModal
        visible={modalState.state === ManageCoursesModalState.ADD_COURSE}
        onClose={handleModalClose}
        degree={degree}
        refetchDegreeCourses={degreeCoursesRefetch}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 32,
    backgroundColor: "#FFFFFF",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flexDirection: "row",
    gap: 12,
  },
  title: {
    color: "#111827",
  },
  exportButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#04060F1A",
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    shadowColor: "#000000",
    shadowOpacity: 0.04,
    borderRadius: 8,
  },
  addStudentButton: {
    borderRadius: 8,
  },
});

export default AdminDegreeManageCoursesContainer;
